/* Original, dependency-free enhancements. Ordinary links and definitions work without JS. */
(() => {
  if (window.CgauHomeMotion) return;
  const active = new Map();
  const selector = '[data-cgau-house],[data-cgau-programme],[data-cgau-home-grades],[data-cgau-fan-arrival]';
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const grades = {
    'brand-new': ['New and unused.', 'Factory-sealed in full original retail packaging.'],
    'new-open-box': ['New and unused.', 'Original packaging has been opened. Check the listing for its exact condition.'],
    'new-box-damaged': ['New and unused.', 'The outer carton has cosmetic damage. That describes the packaging, not wear on the product.'],
    'new-no-box': ['New and unused.', 'The original retail carton is absent. Check the listing for the exact included items.'],
    'manufacturer-renewed': ['Previously used, professionally refurbished and tested through the manufacturer’s renewal programme.', 'Renewed packaging may differ from retail packaging. See the listing for the cosmetic grade and included items.']
  };

  function mount(root) {
    if (active.has(root)) return;
    const lifetime = new AbortController();
    const options = { signal: lifetime.signal };
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const hover = matchMedia('(hover: hover) and (pointer: fine)');
    const animations = new Set();
    let visible = false;
    let disposed = false;
    const canMove = () => visible && !document.hidden && !media.matches && !disposed;
    const stop = () => {
      animations.forEach(animation => animation.cancel());
      animations.clear();
    };
    const animate = (element, frames, timing = {}) => {
      if (!canMove() || !element?.animate) return;
      const animation = element.animate(frames, { duration: 280, easing: ease, ...timing });
      animations.add(animation);
      const remove = () => animations.delete(animation);
      animation.addEventListener('finish', remove, { once: true });
      animation.addEventListener('cancel', remove, { once: true });
    };
    const syncMotion = () => {
      root.toggleAttribute('data-home-motion-off', !canMove());
      if (!canMove()) stop();
    };
    media.addEventListener('change', syncMotion, options);
    document.addEventListener('visibilitychange', syncMotion, options);

    if (root.hasAttribute('data-cgau-house')) {
      const buttons = [...root.querySelectorAll('[data-house-zone]')];
      const guidance = {
        front: ['See who is at the door.', 'Start with a video doorbell, then check its power and chime requirements.'],
        side: ['Watch the side entrance.', 'Start with an outdoor camera, then check Wi-Fi reach and power.'],
        living: ['See what is happening indoors.', 'Choose a camera position and check the power source before buying.'],
        alarms: ['Add a signal beyond the camera.', 'Explore entry, motion and leak sensors. Check the exact sensor’s hub requirements.']
      };
      let selectedZone = null;
      const choose = (button, write = false) => {
        if (!button) return;
        const zone = button.dataset.houseZone;
        const changed = selectedZone !== zone;
        selectedZone = zone;
        root.dataset.zone = zone;
        buttons.forEach(item => {
          item.setAttribute('aria-pressed', String(item === button));
          const mark = item.querySelector('span');
          if (mark) mark.textContent = item === button ? '✓' : '+';
        });
        root.querySelectorAll('[data-house-choice]').forEach(choice => {
          choice.dataset.selected = String(choice.dataset.houseChoice === zone);
        });
        const variants = root.querySelectorAll('[data-house-guidance]');
        variants.forEach(panel => {
          panel.setAttribute('aria-hidden', String(panel.dataset.houseGuidance !== zone));
        });
        // Support cached section markup during the asset rollout.
        if (!variants.length && guidance[zone]) {
          root.querySelector('[data-house-guidance-title]').textContent = guidance[zone][0];
          root.querySelector('[data-house-guidance-copy]').textContent = guidance[zone][1];
        }
        if (changed) {
          stop();
          root.querySelectorAll(`.cgau-house__highlight--${zone} [data-house-pulse],.cgau-house__highlight--${zone} [data-house-alarm-ring]`).forEach((ring, index) => {
            animate(ring, [{ opacity: .9, transform: 'scale(.65)' }, { opacity: 0, transform: 'scale(1.55)' }], { duration: zone === 'alarms' ? 1100 : 560, delay: index * (zone === 'alarms' ? 80 : 40), easing: zone === 'alarms' ? 'linear' : ease });
          });
          const miniSignal = root.querySelector(`[data-house-choice="${zone}"] .cgau-house__mini-signal`);
          animate(miniSignal, [{ opacity: .9, transform: 'scale(.6)' }, { opacity: 0, transform: 'scale(1.6)' }], { duration: zone === 'alarms' ? 1100 : 560, easing: zone === 'alarms' ? 'linear' : ease });
          if (zone === 'side') animate(root.querySelector('.cgau-house__fov'), [{ opacity: 0, transform: 'scale(.4)' }, { opacity: .8, transform: 'scale(1)', offset: .5 }, { opacity: .25, transform: 'scale(1)' }], { duration: 600 });
          if (zone === 'alarms') {
            animate(root.querySelector('.cgau-house__alarm-glow--red'), [{ opacity: .45 }, { opacity: .95, offset: .32 }, { opacity: .15, offset: .68 }, { opacity: .45 }], { duration: 1400, easing: 'ease-in-out' });
            animate(root.querySelector('.cgau-house__alarm-glow--blue'), [{ opacity: .5 }, { opacity: .15, offset: .32 }, { opacity: .95, offset: .68 }, { opacity: .5 }], { duration: 1400, easing: 'ease-in-out' });
          }
        }
        if (write) root.closest('cgau-handwriting-collections')?.select?.(button.dataset.housePhrase);
      };
      buttons.forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => choose(button, true), options);
      });
      root.addEventListener('focusin', event => {
        const choice = event.target.closest('[data-house-choice]');
        if (choice) choose(choice.querySelector('[data-house-zone]'), true);
      }, options);
      root.addEventListener('pointerover', event => {
        if (event.pointerType !== 'mouse' || !hover.matches) return;
        const choice = event.target.closest('[data-house-choice]');
        if (choice && !choice.contains(event.relatedTarget)) choose(choice.querySelector('[data-house-zone]'));
      }, options);
      if (buttons.length) {
        root.dataset.enhanced = 'true';
        choose(buttons[0]);
      }
    }

    if (root.hasAttribute('data-cgau-programme')) {
      root.querySelectorAll('.cgau-listing-showcase__gallery').forEach(gallery => {
      const posters = [...gallery.querySelectorAll('.cgau-listing-showcase__feature')];
      let selected = null;
      const openPoster = poster => {
        if (!poster || poster === selected) return;
        selected = poster;
        gallery.dataset.posterFocus = String(posters.indexOf(poster));
        posters.forEach(item => item.dataset.posterActive = String(item === poster));
        stop();
        const index = [...root.querySelectorAll('.cgau-listing-showcase__feature')].indexOf(poster);
        const prop = poster.querySelector('.cgau-programme__prop i');
        if (index === 0) animate(prop, [{ transform: 'rotate(-75deg)' }, { transform: 'rotate(0)' }], { duration: 480 });
        if (index === 1) animate(prop, [{ transform: 'rotate(-12deg) translateY(-2px)' }, { transform: 'rotate(0) translateY(0)' }], { duration: 400 });
        if (index === 2) animate(prop, [{ transform: 'translateY(-7px) rotate(35deg)' }, { transform: 'translateY(0) rotate(35deg)' }], { duration: 400 });
        if (index > 2) animate(prop, [{ transform: 'translateY(-9px) rotate(-12deg)' }, { transform: 'translateY(2px) rotate(4deg)', offset: .7 }, { transform: 'none' }], { duration: 480 });
      };
      const closePoster = () => {
        selected = null;
        delete gallery.dataset.posterFocus;
        posters.forEach(item => delete item.dataset.posterActive);
        stop();
      };
      posters.forEach(poster => {
        poster.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && hover.matches && !gallery.contains(document.activeElement)) openPoster(poster);
        }, options);
        poster.addEventListener('focusin', () => openPoster(poster), options);
        poster.addEventListener('pointerdown', event => {
          if (event.pointerType === 'touch') openPoster(poster);
        }, options);
      });
      gallery.addEventListener('pointerleave', () => {
        if (!gallery.contains(document.activeElement)) closePoster();
      }, options);
      gallery.addEventListener('focusout', event => {
        if (!gallery.contains(event.relatedTarget)) closePoster();
      }, options);
      });
      const more = root.querySelector('.cgau-listing-showcase__more');
      more?.addEventListener('toggle', () => {
        if (!more.open) return stop();
        more.querySelectorAll('.cgau-listing-showcase__feature').forEach((poster, index) => {
          animate(poster, [{ opacity: .65, transform: 'translateY(9px)' }, { opacity: 1, transform: 'translateY(0)' }], { delay: Math.floor(index / 3) * 60 });
        });
      }, options);
    }

    if (root.hasAttribute('data-cgau-home-grades')) {
      const parcel = root.querySelector('.cgau-grade-parcel');
      const buttons = [...root.querySelectorAll('[data-home-grade]')];
      const supported = buttons.filter(button => grades[button.dataset.homeGrade]);
      // Leave unknown/editor-defined grade labels in the ordinary definitions.
      buttons.filter(button => !grades[button.dataset.homeGrade]).forEach(button => button.hidden = true);
      const choose = button => {
        const key = button.dataset.homeGrade;
        const [unit, packaging] = grades[key];
        parcel.dataset.parcelGrade = key;
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        root.querySelector('[data-parcel-title]').textContent = button.textContent;
        root.querySelector('[data-parcel-unit]').textContent = unit;
        root.querySelector('[data-parcel-packaging]').textContent = packaging;
      };
      supported.forEach(button => button.addEventListener('click', () => choose(button), options));
      if (supported.length) {
        choose(supported[0]);
        parcel.hidden = false;
      }
    }

    const arrive = () => {
      if (!root.hasAttribute('data-cgau-fan-arrival') || root.dataset.pinArrived) return;
      root.dataset.pinArrived = 'true';
      root.querySelectorAll('.cgau-home-fan-intro__pin').forEach((pin, index) => {
        animate(pin, [{ transform: 'translateY(-10px) rotate(-16deg) scale(1.12)' }, { transform: 'translateY(0) rotate(0) scale(1)' }], { duration: 420, delay: index * 70 });
      });
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      syncMotion();
      if (visible) arrive();
    }, { threshold: 0 }) : null;
    syncMotion();
    if (observer) observer.observe(root);
    else { visible = true; syncMotion(); arrive(); }
    active.set(root, () => {
      disposed = true;
      lifetime.abort();
      observer?.disconnect();
      stop();
      active.delete(root);
    });
  }

  function init(scope = document) {
    if (scope.matches?.(selector)) mount(scope);
    scope.querySelectorAll(selector).forEach(mount);
  }
  window.CgauHomeMotion = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of active) if (event.target.contains(root)) dispose();
  });
})();
